<template>
  <div class="c-seasons__wrapper">
    <i
      class="o-icon o-icon--left o-typography--action c-seasons__arrow c-seasons__arrow--left"
      :class="{
        'is-visible': hasPrev,
        'u-desktop-only': showSeasonNumber,
      }"
      @click.prevent="prev"
    ></i>

    <div
      ref="sliderElement"
      class="c-seasons__slider"
    >
      <div class="c-seasons__wrapper js-slider-wrapper">
        <div
          v-for="(record) in seasons.collection.records"
          ref="seasonsElement"
          class="c-seasons__item js-slider-item"
          :class="{ 'has-custom-title': !showSeasonNumber }"
          :title="seasonTitle(record)"
        >
          <div
            class="c-seasons__title o-typography__headline4 o-typography--action o-typography--stateful"
            :class="{ 'is-active': record === season }"
            @click="updateSeason(record)"
          >
            {{ seasonTitle(record) }}
          </div>
        </div>
      </div>
    </div>

    <i
      class="o-icon o-icon--right o-typography--action c-seasons__arrow c-seasons__arrow--right"
      :class="{
        'is-visible': hasNext,
        'u-desktop-only': showSeasonNumber,
      }"
      @click.prevent="next"
    ></i>
  </div>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useContext } from '@/shared/composables';
  import { EventType } from '@/shared/enums';
  import { Slider } from '@/shared/libs';

  const props = defineProps({
    seasons: {
      type: Object,
      required: true,
    },
    season: {
      type: Object,
      required: true,
    },
  });
  const emit = defineEmits(['update:season']);

  const { $EventBus, $i18n } = useContext();

  let slider;
  let widestSlideIndex;
  const sliderElement = ref(null);
  const seasonsElement = ref(null);
  const hasNext = ref(false);
  const hasPrev = ref(false);

  const showSeasonNumber = computed(() => props.season.serial.showSeasonNumber);

  function updateSeason(record) {
    emit('update:season', record);
  }

  function attachSlider() {
    destroySlider();
    slider = Slider(sliderElement.value);
    slider.on('change', () => updateSliderInfo());
    updateSliderInfo();

    const itemsWidth = seasonsElement.value.map((item) => item.getBoundingClientRect().width || item.offsetWidth);
    widestSlideIndex = itemsWidth.indexOf(Math.max(...itemsWidth));

    updateSlidesWidth();
  }

  function destroySlider() {
    if (slider) {
      slider.reset();
      slider.destroy();
    }
  }

  function updateSliderInfo() {
    hasNext.value = slider.hasNext();
    hasPrev.value = slider.hasPrev();
  }

  function updateSlider() {
    updateSlidesWidth();
    slider.update();
  }

  function updateSlidesWidth() {
    const seasons = seasonsElement.value;
    const widestSlide = seasons[widestSlideIndex];
    const widestSlideWidth = widestSlide.getBoundingClientRect().width || widestSlide.offsetWidth;

    seasons.forEach((slide, index) => {
      if (index !== widestSlideIndex) {
        slide.style.width = `${widestSlideWidth}px`;
      }
    });
  }

  function prev() {
    slider.prev();
  }

  function next() {
    slider.next();
  }

  function seasonTitle(record) {
    const defaultTitle = `${$i18n.t('glossary.season')} ${record.display}`;
    return showSeasonNumber.value ? defaultTitle : record.title || defaultTitle;
  }

  onMounted(() => {
    attachSlider();
    $EventBus.on(EventType.RESIZE, updateSlider);
  });

  onBeforeUnmount(() => {
    destroySlider();
    $EventBus.off(EventType.RESIZE, updateSlider);
  });
</script>
